import * as React from 'react';
import { useSearchParams } from 'react-router-dom';
import { STATUS_BUILD } from '@/data';
import type { FiltrosFeed, StatusBuild } from '@/data/types';
import { usePodeVer } from '@/hooks/use-acesso';
import { periodoDaUrl } from '@/lib/periodo';

const ATRASO_BUSCA_MS = 300;

function lerStatus(params: URLSearchParams): StatusBuild[] {
  return params.getAll('status').filter((valor): valor is StatusBuild => STATUS_BUILD.includes(valor as StatusBuild));
}

/**
 * Estado de filtros do feed, todo na URL (`?ramo=2602&status=reprovado&de=2026-10-01&ate=2026-10-23…`):
 * qualquer recorte é um link que se manda no chat. As duas vistas (Timeline e
 * Feed) usam o MESMO estado, a mesma consulta e a mesma paginação.
 */
export function useFiltrosFeed(ramoFixo?: string) {
  const [params, setParams] = useSearchParams();
  const podeIncidentes = usePodeVer('incidentes');

  const produto = params.get('produto');
  const ramo = ramoFixo ?? params.get('ramo');
  const branch = params.get('branch');
  const statusParam = params.getAll('status').join(',');
  // Memo pela string: `getAll` devolve um array novo a cada render, e o feed
  // recomeçaria do topo a cada render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const status = React.useMemo(() => lerStatus(params), [statusParam]);
  const q = params.get('q') ?? '';
  const comIncidente = podeIncidentes && params.get('com_incidente') === 'true';
  const versaoAberta = params.get('versao');
  const de = params.get('de');
  const ate = params.get('ate');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const periodo = React.useMemo(() => periodoDaUrl(params), [de, ate]);

  const atualizar = React.useCallback(
    (mudancas: Record<string, string | string[] | null>) => {
      setParams(
        (atual) => {
          const proximo = new URLSearchParams(atual);
          for (const [chave, valor] of Object.entries(mudancas)) {
            proximo.delete(chave);
            if (Array.isArray(valor)) valor.forEach((item) => proximo.append(chave, item));
            else if (valor) proximo.set(chave, valor);
          }
          return proximo;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  // A busca digitada só vai para a URL (e dispara a consulta) depois de uma
  // pausa — cada tecla não pode virar uma requisição.
  const [busca, setBusca] = React.useState(q);
  React.useEffect(() => setBusca(q), [q]);
  React.useEffect(() => {
    if (busca.trim() === q) return;
    const id = window.setTimeout(() => atualizar({ q: busca.trim() || null }), ATRASO_BUSCA_MS);
    return () => window.clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busca]);

  const abrirVersao = React.useCallback(
    (versao: string) => {
      setParams((atual) => {
        const proximo = new URLSearchParams(atual);
        proximo.set('versao', versao);
        return proximo;
      });
    },
    [setParams],
  );

  const filtros: FiltrosFeed = React.useMemo(
    () => ({
      produto,
      ramo,
      branch,
      status,
      q: q || null,
      com_incidente: comIncidente || undefined,
      publicado_de: periodo.de,
      publicado_ate: periodo.ate,
    }),
    [produto, ramo, branch, status, q, comIncidente, periodo],
  );

  const quantidade = [
    produto,
    ramoFixo ? null : ramo,
    branch,
    q,
    status.length ? 'status' : null,
    comIncidente ? 'incidente' : null,
    periodo.de || periodo.ate ? 'periodo' : null,
  ].filter(Boolean).length;

  function limpar() {
    setBusca('');
    atualizar({ produto: null, ramo: null, branch: null, status: null, q: null, com_incidente: null, de: null, ate: null });
  }

  return {
    produto,
    ramo,
    branch,
    status,
    q,
    comIncidente,
    versaoAberta,
    periodo,
    busca,
    setBusca,
    atualizar,
    abrirVersao,
    filtros,
    quantidade,
    limpar,
    podeIncidentes,
  };
}

export type FiltrosFeedEstado = ReturnType<typeof useFiltrosFeed>;
